import { useState } from "react";
import { toast } from "sonner";
import { useSuggestResource } from "@/features/resources/resources";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

/**
 * Shared "suggest a resource" dialog → admin review queue. When opened from a
 * discussion, threadId + prefillUrl tie the submission back to the
 * conversation. Pass a changing `key` (e.g. the prefill URL) to reset the form.
 */
export function SuggestResourceDialog({
  open,
  onOpenChange,
  threadId,
  prefillUrl,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId?: number;
  prefillUrl?: string;
}) {
  const suggest = useSuggestResource();
  const [form, setForm] = useState({
    title: "",
    description: "",
    url: prefillUrl ?? "",
    category: "",
    tags: "",
  });

  function handleSubmit() {
    suggest.mutate(
      {
        title: form.title,
        description: form.description,
        url: form.url,
        category: form.category || "Community",
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        thread_id: threadId,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setForm({
            title: "",
            description: "",
            url: "",
            category: "",
            tags: "",
          });
          toast.success("Submitted for review");
        },
        onError: (err) => toast.error(err.message),
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Suggest a resource</DialogTitle>
          <DialogDescription>
            {threadId
              ? "This suggestion will be linked to the current discussion and reviewed before it appears in the library."
              : "Submissions are reviewed by an administrator before they appear in the library."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>URL</Label>
            <Input
              placeholder="https://…"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Category</Label>
              <Input
                placeholder="e.g. Legal"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={
              !form.title || !form.url || !form.description || suggest.isPending
            }
          >
            {suggest.isPending && <Spinner data-slot="icon" />}
            Submit for review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SuggestResourceDialog;
